import Logo from "../../components/Logo";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router";
import FormInput from "../../components/FormInput";
import Button from "../../components/Button";
import { motion } from "motion/react";
import { useAuth } from "../../hooks/useAuth";
import Redirect from "../../components/Redirect";
import FullLoading from "../../components/FullLoading";

const signUpSchema = z.object({
  firstName: z.string().min(2, "Le prénom doit contenir au moins 2 caractères"),
  lastName: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
  email: z.string().email("Adresse email invalide"),
  password: z
    .string()
    .min(6, "Le mot de passe doit contenir au moins 6 caractères"),
});

export type SignUpFormData = z.infer<typeof signUpSchema>;

export default function SignUpPage() {
  const { isAuthenticated, isAuthLoading, signUp } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
  });

  const onSubmit = (data: SignUpFormData) => {
    signUp(data);
  };

  if (isAuthLoading) {
    return <FullLoading />;
  }

  if (isAuthenticated) {
    const invitedToOrganizationId = localStorage.getItem(
      "invitedToOrganizationId"
    );
    const invitationCode = localStorage.getItem("invitationCode");

    if (invitedToOrganizationId && invitationCode) {
      return (
        <Redirect to={`/join-organization?invitationCode=${invitationCode}`} />
      );
    }

    return <Redirect to="/create-organization" />;
  }

  return (
    <div
      className="flex flex-col justify-center items-center h-screen gap-4 bg-background"
      data-testid="signup-page"
    >
      <Logo />

      <motion.div
        className="w-full max-w-md bg-dark border border-border rounded-lg px-4 py-8 text-primary"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-2xl font-bold mb-6 text-center">Créer un compte</h1>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormInput
            id="firstName"
            label="Prénom"
            register={register}
            error={errors.firstName}
            dataTestId="firstName"
          />

          <FormInput
            id="lastName"
            label="Nom"
            register={register}
            error={errors.lastName}
            dataTestId="lastName"
          />

          <FormInput
            id="email"
            label="Email"
            type="email"
            register={register}
            error={errors.email}
            dataTestId="email"
          />

          <FormInput
            id="password"
            label="Mot de passe"
            type="password"
            register={register}
            error={errors.password}
            dataTestId="password"
          />
          <Button
            variant="primary"
            className="w-full h-12 mt-8"
            type="submit"
            dataTestId="signup-button"
          >
            S'inscrire
          </Button>
          <div className="text-center text-tertiary text-sm">
            Déjà un compte ?{" "}
            <span className="text-accent">
              <Link to="/signin">Se connecter</Link>
            </span>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
